import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { TermsAgree, TERMS_VERSION } from "@/components/TermsAgree";

export default function PayDrop() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);

  useEffect(() => {
    api.get(`/drop-invoices/${id}`).then(({ data }) => setInv(data)).catch(() => setErr("We couldn't find that drop. The link may be out of date."));
  }, [id]);

  const pay = async () => {
    if (!agree) return toast.error("Please accept the Terms of Service to continue");
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/drop-invoice/${id}`, { origin_url: window.location.origin, terms_accepted: true, terms_version: TERMS_VERSION });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-16 bg-sam-cream/40">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-7 shadow-sm" data-testid="pay-drop-card">
        <div className="flex items-center gap-2 text-sam-crimson font-bold text-xs uppercase tracking-widest"><CalendarClock className="h-4 w-4" />Postcard drop payment</div>
        {err && <p className="mt-4 text-sam-slate" data-testid="pay-drop-error">{err}</p>}
        {!err && !inv && <Loader2 className="h-5 w-5 animate-spin mt-6 text-sam-navy" />}
        {inv && (
          <>
            <h1 className="mt-3 font-display text-2xl font-black text-sam-navy">{inv.business_name}</h1>
            <p className="text-sm text-sam-slate mt-1">Drop {inv.drop_month} · quadrant {inv.quadrant}{inv.central_zip ? ` · ZIP ${inv.central_zip}` : ""}</p>
            {inv.deadline && <p className="text-xs text-sam-slate mt-1">Due by {String(inv.deadline).slice(0, 10)}</p>}
            {inv.status === "paid" ? (
              <div className="mt-6 flex items-center gap-2 text-sam-green font-semibold" data-testid="pay-drop-paid"><CheckCircle2 className="h-5 w-5" />This drop is paid — you're all set.</div>
            ) : (
              <>
                <div className="mt-5 flex items-end justify-between rounded-xl bg-sam-navy text-sam-cream p-4">
                  <span className="text-sm uppercase tracking-wide text-sam-cream/70">Amount due</span>
                  <span className="font-display text-3xl font-black text-sam-amber" data-testid="pay-drop-amount">{money(inv.amount)}</span>
                </div>
                <Button className="w-full mt-5 bg-sam-crimson hover:bg-sam-crimson/90" onClick={pay} disabled={busy || !agree} data-testid="pay-drop-btn">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Pay this drop
                </Button>
                <div className="mt-3"><TermsAgree checked={agree} onChange={setAgree} testid="pay-drop-terms" /></div>
                <p className="mt-3 text-[11px] text-sam-slate text-center">Secure checkout by Stripe · Saving America Money</p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
